'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import Member from '@/components/Product/Member';
import ProductTag from '@/components/Product/ProductTag';
import Wish from '@/components/Product/Wish';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface ProductItem {
  id: number;
  name: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
    celebrity?: string[];
    product_gray?: string[];
  };
  price_info?: Array<{
    currency: string;
    price: string;
    sell_price: string;
  }>;
  is_wish?: number;
  wish_num?: number;
  is_ad?: number;
  image_url?: string;
  pc_image?: string;
  wap_image?: string;
  jump_url?: string;
  big_head_image?: string;
  card_type?: number;
  status?: number;
  stock?: number;
  products_promotions_type?: string;
  is_new_in?: number;
  product_size?: string;
  is_hot?: number;
  // 会员折扣相关字段
  discount_percent_v0?: number;
  discount_percent_v1?: number;
  discount_percent_v2?: number;
  discount_percent_v3?: number;
  [key: string]: any;
}

interface SparklyProductListProps {
  pageData: {
    product?: {
      list: ProductItem[];
    };
    ad_image?: ProductItem[];
    big_head_image?: Array<{
      shop_sku: string;
      image_url: string;
    }>;
    group_limit?: number;
  };
  deviceType: EPlatform;
  onProductExp: (sensorData: {
    item: ProductItem;
    index: number;
    module_name: string;
    image_type: string;
  }) => void;
  onProductClick: (sensorData: {
    item: ProductItem;
    index: number;
    module_name: string;
    image_type: string;
  }) => void;
  onAdExp: (adData: { item: ProductItem; index: number }) => void;
  onAdClick: (adData: { item: ProductItem; index: number }) => void;
}

// 获取产品尺寸文本的辅助函数
const getProductSizeText = (size?: string): string => {
  if (size === 'S') return 'Narrow';
  if (size === 'M') return 'Medium';
  if (size === 'L') return 'Wide';
  return size || '';
};

export default function SparklyProductList({
  pageData,
  deviceType,
  onProductExp,
  onProductClick,
  onAdExp,
  onAdClick,
}: SparklyProductListProps) {
  const [dataList, setDataList] = useState<ProductItem[]>([]);
  const isWAP = deviceType !== EPlatform.WEB;
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  // 处理产品和广告位的合并逻辑
  useEffect(() => {
    if (pageData?.product?.list) {
      const finalList: ProductItem[] = [];
      let adIndex = 0;
      let productIndex = 0;
      const { big_head_image = [], ad_image = [] } = pageData;

      if (pageData.product.list.length && ad_image.length && big_head_image.length) {
        // 根据设备类型设置广告位置（完全按Vue版本）
        let adsPosition = isWAP
          ? [0, 11, 18, 27] // 移动端广告位置
          : [0, 13, 18, 29]; // PC端广告位置
        const isThirdAddTwoPits = ad_image.length && ad_image[2].card_type === 2; // 第三个广告位占据两个坑位
        if (isThirdAddTwoPits) {
          adsPosition = [0, 11, 18, 29];
        }
        const limit = pageData.group_limit ? pageData.group_limit + 4 : 38;

        for (let i = 0; i < limit; i++) {
          if (adsPosition.includes(i)) {
            // 插入广告的位置
            if (adIndex < ad_image.length) {
              // 添加广告位，确保image_url字段正确
              const adItem = {
                ...ad_image[adIndex],
                is_ad: 1,
                id: 9999000 + adIndex, // 使用数字ID
                // 保持原始的image_url字段，这个是Vue版本中用来识别广告位的关键字段
                image_url: ad_image[adIndex].image_url,
              };
              finalList.push(adItem);
              adIndex++;
            } else if (productIndex < pageData.product.list.length) {
              // 如果没有更多的广告，则继续插入商品
              finalList.push(pageData.product.list[productIndex]);
              productIndex++;
            }
          } else if (productIndex < pageData.product.list.length) {
            finalList.push(pageData.product.list[productIndex]);
            productIndex++;
          }
        }

        // 处理大头照 - 按照Vue版本通过shop_sku匹配
        finalList.forEach((item) => {
          if (!item.is_ad) {
            // 只处理商品项
            const hasBigHead = big_head_image.find(
              (bItem: { shop_sku: string; image_url: string }) => bItem.shop_sku === item.shop_sku,
            );
            if (hasBigHead) {
              Object.assign(item, { big_head_image: hasBigHead.image_url });
            }
          }
        });
      } else {
        // 如果没有广告配置，直接使用产品列表
        const productList = [...pageData.product.list];
        // 处理大头照
        productList.forEach((item) => {
          const hasBigHead = big_head_image.find(
            (bItem: { shop_sku: string; image_url: string }) => bItem.shop_sku === item.shop_sku,
          );
          if (hasBigHead) {
            Object.assign(item, { big_head_image: hasBigHead.image_url });
          }
        });
        finalList.push(...productList);
      }

      setDataList(finalList);
    }
  }, [pageData, isWAP]);

  // 获取广告在数组中的索引（完全按Vue版本）
  const adIndex = (item: ProductItem) => {
    if (item.is_ad !== 1) return -1;
    const adImages = pageData?.ad_image || [];
    return adImages.findIndex((aItem: any) => aItem.image_url === item.image_url);
  };

  const handleProductClick = (item: ProductItem, index: number) => {
    // 先执行埋点上报
    onProductClick({
      item,
      index,
      module_name: '新品系列',
      image_type: item.big_head_image ? '配置图' : '商品图',
    });

    // 然后跳转到商品详情页
    if (item.shop_sku) {
      navigate(`/goods-detail/${item.shop_sku}`);
    }
  };

  const handleAdClick = (item: ProductItem, index: number) => {
    // 执行埋点上报，跳转逻辑由MagicJump组件处理
    onAdClick({ item, index });
  };

  const handleProductInView = (item: ProductItem, index: number) => {
    onProductExp({
      item,
      index,
      module_name: '新品系列',
      image_type: item.big_head_image ? '配置图' : '商品图',
    });
  };

  const handleAdInView = (item: ProductItem, index: number) => {
    onAdExp({ item, index });
  };

  // 获取商品价格信息 - 根据当前币种和用户信息获取正确的价格
  const getProductPriceInfo = (item: ProductItem) => {
    try {
      return getPriceInfo(userInfo, item, currency);
    } catch {
      return { price: '', sell_price: '' };
    }
  };

  return (
    <div className="w-full bg-white">
      <ul
        className={`
          min-h-[50vh] grid relative w-full
          ${isWAP ? 'grid-cols-2 gap-[1px] pb-10' : 'grid-cols-4 gap-y-[1px] gap-x-[1px] pb-[68px]'}
        `}
        style={{ backgroundColor: dataList.length === 0 ? '#ffffff' : 'bg-[#eeeeee]' }}
      >
        {dataList.map((item, index) => {
          const adIdx = adIndex(item);

          // 曝光检测Hook
          const ExposureDetector = ({ children }: { children: React.ReactNode }) => {
            const [expRef, inView] = useInView({
              threshold: 0.8,
              triggerOnce: true,
            });

            // 当进入视野时触发曝光埋点
            useEffect(() => {
              if (inView) {
                if (item.is_ad === 1) {
                  handleAdInView(item, index);
                } else {
                  handleProductInView(item, index);
                }
              }
            }, [inView]);

            return (
              <div ref={expRef} className="w-full h-full">
                {children}
              </div>
            );
          };

          return (
            <li
              key={item.id || index}
              className={`
                ${item.is_ad === 1 ? 'relative' : ''}
                ${adIdx === 0 ? 'col-span-2' : ''}
                ${adIdx === 1 ? 'col-span-2' : ''}
                ${adIdx === 2 ? 'col-span-2' : ''}
                ${adIdx === 3 ? 'col-span-2' : ''}
                ${adIdx === 2 && !isWAP && item.card_type !== 2 ? 'row-span-2' : ''}
              `}
              style={{
                boxShadow: '0 0 0 1px #282828',
                // PC端第三个广告位的特殊网格位置
                ...(adIdx === 2 && !isWAP && item.card_type !== 2
                  ? {
                      // 只有当 card_type 不是 2 时，才使用固定位置（占两行）
                      gridColumn: '1 / 3',
                      gridRow: '6 / 8',
                    }
                  : {}),
              }}
            >
              <ExposureDetector>
                {item.is_ad === 1 ? (
                  // 广告位 - 使用MagicJump组件包裹
                  (() => {
                    // 检查是否为PC端第三个广告位且card_type为2
                    const isSpecialAdFormat = adIdx === 2 && !isWAP && item.card_type === 2;

                    if (isSpecialAdFormat) {
                      // PC端第三个广告位card_type=2时，使用响应式高度容器
                      return (
                        <div className="relative w-full h-full" style={{ paddingBottom: '58%' }}>
                          <MagicJump
                            item={{ jump_type: item.jump_type, Jump_target: item.jump_value }}
                            onJumpClick={() => handleAdClick(item, index)}
                            className="absolute inset-0 w-full h-full block overflow-hidden"
                          >
                            <Image
                              src={`${item.pc_image || item.image_url}?imbypass=true`}
                              alt="advertisement"
                              width={600}
                              height={300}
                              unoptimized={!isWAP}
                              className="w-full h-full block object-cover object-center"
                            />
                          </MagicJump>
                        </div>
                      );
                    }

                    // 其他广告位的处理逻辑
                    const getAdHeight = () => {
                      if (isWAP) {
                        return adIdx === 2 && (!item.card_type || item.card_type !== 2)
                          ? 'h-[375px]'
                          : 'h-[224px]';
                      }
                      return 'absolute inset-0 h-full';
                    };

                    const getImageDimensions = () => {
                      if (isWAP) {
                        return {
                          width: 400,
                          height:
                            adIdx === 2 && (!item.card_type || item.card_type !== 2) ? 375 : 224,
                        };
                      }
                      return { width: 600, height: 400 };
                    };

                    const dimensions = getImageDimensions();

                    return (
                      <MagicJump
                        item={{ jump_type: item.jump_type, Jump_target: item.jump_value }}
                        onJumpClick={() => handleAdClick(item, index)}
                        className={`block relative overflow-hidden ${getAdHeight()}`}
                      >
                        <Image
                          src={`${isWAP ? item.wap_image || item.image_url : item.pc_image || item.image_url}?imbypass=true`}
                          alt="advertisement"
                          width={dimensions.width}
                          unoptimized={!isWAP}
                          height={dimensions.height}
                          className="w-full h-full block object-cover object-center"
                        />
                      </MagicJump>
                    );
                  })()
                ) : (
                  // 产品卡片 - 自定义样式（按Vue版本实现）
                  <div
                    className={`product-card relative overflow-hidden cursor-pointer bg-[#eeeeee] group ${isWAP ? 'h-[238px]' : 'h-full'}`}
                    style={{ paddingBottom: isWAP ? undefined : 'calc(100% + 72px)' }}
                  >
                    <Link
                      href={`/goods-detail/${item.shop_sku}`}
                      className={`
                      product-card-content block absolute inset-0 bg-[#eeeeee]
                      ${item.status === 2 || (item.stock !== undefined && item.stock <= 0) ? 'sold_out' : ''}
                      ${!isWAP && item.big_head_image ? 'hide_content' : ''}
                    `}
                      style={{
                        backgroundPosition: 'center center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleProductClick(item, index);
                      }}
                    >
                      {/* 产品图片区域 */}
                      <div
                        className={`
                      product_image absolute w-full flex items-center justify-center
                      ${
                        item.big_head_image && !isWAP
                          ? 'h-full overflow-visible'
                          : `${isWAP ? 'pb-[100%]' : 'pb-[100%]'} overflow-hidden`
                      }
                    `}
                      >
                        {(() => {
                          // 完全按照Vue版本的逻辑
                          const hoverBackgroundImage = isWAP ? false : item.big_head_image;

                          // productImg逻辑 - 按Vue版本优先级：hoverBackgroundImage > product_gray[0] > product[0]
                          let productImg = { image_url: '', is_head: 0 };
                          const { product = [], product_gray = [] } = item.image || {};

                          if (hoverBackgroundImage) {
                            productImg = { image_url: hoverBackgroundImage, is_head: 1 };
                          } else if (product_gray.length > 0) {
                            productImg = { image_url: product_gray[0], is_head: 0 };
                          } else if (product.length > 0) {
                            productImg = { image_url: product[0], is_head: 0 };
                          }

                          // productImg2逻辑 (仅PC端) - 按Vue版本逻辑
                          let productImg2 = { image_url: '', is_celebrity: 0 };
                          if (!isWAP) {
                            // 如果有hoverBackgroundImage且有其他图片，优先显示灰底图，否则普通图
                            if (
                              hoverBackgroundImage &&
                              (product_gray.length > 0 || product.length > 0)
                            ) {
                              if (product_gray.length > 0) {
                                productImg2 = { image_url: product_gray[0], is_celebrity: 0 };
                              } else {
                                productImg2 = { image_url: product[0], is_celebrity: 0 };
                              }
                            }
                            // 如果有灰底图，优先使用灰底图
                            else if (product_gray.length > 1) {
                              // 如果有多张灰底图，第二张用第二张灰底图
                              productImg2 = { image_url: product_gray[1], is_celebrity: 0 };
                            } else if (product_gray.length === 1) {
                              // 如果只有一张灰底图，第二张不显示（避免重复）
                              productImg2 = { image_url: '', is_celebrity: 0 };
                            }
                            // 如果没有灰底图但有多张普通商品图，选择第二张普通商品图
                            else if (product.length > 1) {
                              productImg2 = { image_url: product[1], is_celebrity: 0 };
                            }
                          }

                          // 判断当前显示的图片是否为白底商品图
                          const isWhiteBackgroundImage = (() => {
                            // 如果有hoverBackgroundImage，是白底图
                            if (hoverBackgroundImage) {
                              return true;
                            }
                            // 如果有灰底图，说明优先显示灰底图，不是白底图
                            if (product_gray.length > 0) {
                              return false;
                            }
                            // 如果只有普通商品图，是白底图
                            return product.length > 0;
                          })();

                          // 判断第二张图片（hover图片）是否为白底商品图
                          const isSecondImageWhite = (() => {
                            // 如果有hoverBackgroundImage且显示的是普通商品图
                            if (
                              hoverBackgroundImage &&
                              product_gray.length === 0 &&
                              product.length > 0
                            ) {
                              return true;
                            }
                            // 如果有灰底图，第二张图片不是白底图
                            if (product_gray.length > 0) {
                              return false;
                            }
                            // 如果没有灰底图但有多张普通商品图，第二张是白底图
                            if (product.length > 1) {
                              return true;
                            }
                            // 其他情况不是白底图
                            return false;
                          })();

                          return (
                            <>
                              {/* 第一张图片 - productImg */}
                              {productImg.image_url && (
                                <Image
                                  src={productImg.image_url}
                                  alt={`${item.short_name || item.name} good-img`}
                                  width={isWAP ? 200 : 300}
                                  height={isWAP ? 200 : 300}
                                  className={`
                                  good-img transition-opacity
                                  ${
                                    productImg.is_head === 1
                                      ? 'good-img-head relative w-full h-full object-cover object-center'
                                      : 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] opacity-100'
                                  }
                                  ${!isWAP && productImg2.image_url ? 'group-hover:opacity-0' : ''}
                                `}
                                  style={{
                                    mixBlendMode: (() => {
                                      if (productImg.is_head === 1) return 'normal';
                                      return isWhiteBackgroundImage ? 'multiply' : 'normal';
                                    })(),
                                    ...(productImg.is_head === 1
                                      ? { left: 'auto', top: 'auto', transform: 'none' }
                                      : {}),
                                  }}
                                />
                              )}

                              {/* 第二张图片 - productImg2 (仅PC端) */}
                              {!isWAP && productImg2.image_url && (
                                <Image
                                  src={productImg2.image_url}
                                  alt={`${item.short_name || item.name} good-img`}
                                  width={300}
                                  height={300}
                                  className={`
                                  good-img good-img-hover absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                                  opacity-0 group-hover:opacity-100 transition-opacity
                                  ${productImg2.is_celebrity === 1 ? 'good-img-celebrity w-full' : 'w-[70%]'}
                                  ${productImg.is_head === 1 ? '-translate-y-[calc(50%+36px)]' : ''}
                                `}
                                  style={{
                                    mixBlendMode: (() => {
                                      if (productImg2.is_celebrity === 1) return 'normal';
                                      return isSecondImageWhite ? 'multiply' : 'normal';
                                    })(),
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}

                        {/* 头部标签区域 */}
                        <div
                          className={`header absolute top-0 left-0 w-full flex justify-between items-center box-border ${
                            isWAP ? 'p-[8px]' : 'px-[12px] pt-[12px] pl-[16px]'
                          } ${!isWAP && item.big_head_image ? 'hidden group-hover:flex' : 'flex'}`}
                        >
                          {/* 标签区域 - 会员标签优先，其次是促销标签 */}
                          <div className="special-tags flex h-[24px] items-center">
                            {token && userInfo.id && userInfo.member_level > 0 ? (
                              // 会员用户优先显示会员标签，Member组件内部会判断是否有折扣
                              <>
                                <Member level={userInfo.member_level} productData={item} />
                                {/* 如果没有会员折扣，Member组件会返回空，这时显示促销标签 */}
                                {(!item[`discount_percent_v${userInfo.member_level}`] ||
                                  item[`discount_percent_v${userInfo.member_level}`] <= 0) && (
                                  <ProductTag product={item} hideBestSellerTag />
                                )}
                              </>
                            ) : (
                              // 非会员用户显示促销标签
                              <ProductTag product={item} hideBestSellerTag />
                            )}
                          </div>

                          {/* 收藏按钮 */}
                          <Wish
                            data={item}
                            className={`ml-auto leading-[${isWAP ? '18px' : '24px'}] text-auxiliary-dark-gray ${isWAP ? 'text-12' : 'text-14'}`}
                            iconClassName={`text-${isWAP ? '18' : '24'}`}
                            iconSize={isWAP ? '18px' : '24px'}
                            showWishNum={false}
                            loginSituation="专题页收藏商品"
                            collectScene="新品系列专题页"
                          />
                        </div>

                        {/* 售罄状态 */}
                        {(item.status === 2 || (item.stock !== undefined && item.stock <= 0)) && (
                          <div className="sold-out-block-wrapper absolute inset-0 bg-[rgba(245,245,245,0.5)]">
                            <div
                              className={`
                            sold-out-block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                            bg-[rgba(0,0,0,0.5)] text-white text-center rounded-full flex items-center justify-center
                            ${isWAP ? 'w-[65px] h-[65px] text-[12px]' : 'w-[100px] h-[100px] text-[14px]'}
                          `}
                            >
                              {(() => {
                                const isNoRestock = item.status === 2;
                                if (isNoRestock) {
                                  return isWAP ? 'No\nRestock' : 'No Restock';
                                }
                                return isWAP ? 'Sold\nOut' : 'Sold Out';
                              })()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 产品信息区域 */}
                      <div
                        className={`
                      product_info absolute left-0 bottom-0 w-full box-border
                      ${isWAP ? 'h-[50px] px-[10px]' : 'h-[72px] px-[16px]'}
                      ${!isWAP && item.big_head_image ? 'hidden group-hover:block' : 'block'}
                    `}
                      >
                        <section className="flex items-center justify-between">
                          <p
                            className={`
                          name flex-1 text-[#282828] overflow-hidden text-ellipsis whitespace-nowrap
                          ${isWAP ? 'text-[12px] leading-[12px]' : 'text-[14px] leading-[16px]'}
                        `}
                          >
                            {item.short_name || item.name}
                          </p>
                          {!isWAP && item.product_size && (
                            <div className="flex items-center">
                              <i className="iconfont icon-size text-[20px] -translate-y-[2px]" />
                              <span className="text-[12px] ml-[6px]">
                                {getProductSizeText(item.product_size)}
                              </span>
                            </div>
                          )}
                        </section>

                        <section
                          className={`price_wrapper flex items-baseline flex-wrap ${
                            isWAP ? 'mt-[8px]' : 'mt-[12px] pb-[20px]'
                          }`}
                        >
                          {(() => {
                            const priceData = getProductPriceInfo(item);
                            return (
                              <>
                                <span
                                  className={`
                                    now-price text-[#282828] font-bb mr-[6px]
                                    ${isWAP ? 'text-[14px] mr-[6px]' : 'text-[16px] mr-[8px]'}
                                  `}
                                >
                                  {formatPriceNumberWithSymbol(
                                    priceData.sell_price || priceData.price || '0.00',
                                    currency,
                                  )}
                                </span>
                                {priceData.sell_price !== priceData.price && priceData.price && (
                                  <span
                                    className={`
                                      old-price text-[#a9a9a9] line-through
                                      ${isWAP ? 'text-[12px]' : 'text-[14px]'}
                                    `}
                                  >
                                    {formatPriceNumberWithSymbol(priceData.price, currency)}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </section>
                      </div>
                    </Link>
                  </div>
                )}
              </ExposureDetector>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
